import React, { useCallback, useMemo } from "react";
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Input,
  Button,
  Checkbox,
  Grid,
  DatePicker,
  ColumnLayout,
} from "@cloudscape-design/components";
import { RealmSelector } from "../common";
import { Realm, Stage, REDRIVE_LABELS } from "../../constants";

export interface RedriveConfig {
  stateMachineName: string;
  realm: Realm;
  override: boolean;
  resume: boolean;
  redriveFailed: boolean;
  redriveAborted: boolean;
  redriveTimedOut: boolean;
  redriveRunning: boolean;
  startDate: string;
  endDate: string;
}

interface RedriveConfigFormProps {
  config: RedriveConfig;
  stage: Stage;
  loading: boolean;
  onConfigChange: (config: RedriveConfig) => void;
  onSubmit: () => void;
}

const GRID_MAIN = [{ colspan: 8 }, { colspan: 4 }] as const;
const GRID_DATES = [{ colspan: 6 }, { colspan: 6 }] as const;

export const RedriveConfigForm: React.FC<RedriveConfigFormProps> = ({ config, stage, loading, onConfigChange, onSubmit }) => {
  const update = useCallback((partial: Partial<RedriveConfig>): void => onConfigChange({ ...config, ...partial }), [
    config,
    onConfigChange,
  ]);

  const isDisabled = useMemo(() => !config.stateMachineName.trim(), [config.stateMachineName]);

  return (
    <Container header={<Header variant="h2">{REDRIVE_LABELS.CONFIGURATION}</Header>}>
      <SpaceBetween size="m">
        <Grid gridDefinition={[...GRID_MAIN]}>
          <FormField label={REDRIVE_LABELS.STATE_MACHINE_NAME}>
            <Input
              value={config.stateMachineName}
              onChange={(e) => update({ stateMachineName: e.detail.value })}
              placeholder={REDRIVE_LABELS.STATE_MACHINE_PLACEHOLDER}
            />
          </FormField>
          <FormField label={REDRIVE_LABELS.REALM}>
            <RealmSelector value={config.realm} stage={stage} onChange={(realm) => update({ realm })} />
          </FormField>
        </Grid>

        <Grid gridDefinition={[...GRID_DATES]}>
          <FormField label={REDRIVE_LABELS.START_DATE}>
            <DatePicker
              value={config.startDate}
              onChange={(e) => update({ startDate: e.detail.value })}
              placeholder={REDRIVE_LABELS.DATE_PLACEHOLDER}
            />
          </FormField>
          <FormField label={REDRIVE_LABELS.END_DATE}>
            <DatePicker
              value={config.endDate}
              onChange={(e) => update({ endDate: e.detail.value })}
              placeholder={REDRIVE_LABELS.DATE_PLACEHOLDER}
            />
          </FormField>
        </Grid>

        <ColumnLayout columns={3}>
          <SpaceBetween size="xs">
            <Checkbox checked={config.override} onChange={(e) => update({ override: e.detail.checked })}>
              {REDRIVE_LABELS.OVERRIDE}
            </Checkbox>
            <Checkbox checked={config.resume} onChange={(e) => update({ resume: e.detail.checked })}>
              {REDRIVE_LABELS.RESUME}
            </Checkbox>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Checkbox checked={config.redriveFailed} onChange={(e) => update({ redriveFailed: e.detail.checked })}>
              {REDRIVE_LABELS.REDRIVE_FAILED}
            </Checkbox>
            <Checkbox checked={config.redriveAborted} onChange={(e) => update({ redriveAborted: e.detail.checked })}>
              {REDRIVE_LABELS.REDRIVE_ABORTED}
            </Checkbox>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <Checkbox checked={config.redriveTimedOut} onChange={(e) => update({ redriveTimedOut: e.detail.checked })}>
              {REDRIVE_LABELS.REDRIVE_TIMED_OUT}
            </Checkbox>
            <Checkbox checked={config.redriveRunning} onChange={(e) => update({ redriveRunning: e.detail.checked })}>
              {REDRIVE_LABELS.REDRIVE_RUNNING}
            </Checkbox>
          </SpaceBetween>
        </ColumnLayout>

        <Button variant="primary" onClick={onSubmit} loading={loading} disabled={isDisabled}>
          {REDRIVE_LABELS.START_REDRIVE}
        </Button>
      </SpaceBetween>
    </Container>
  );
};
